import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Tasks from "./pages/Tasks";
import Users from "./pages/Users";
import Invoices from "./pages/Invoices";
import ClientDashboard from "./pages/ClientDashboard";
import CalendarView from "./pages/CalendarView";
import GanttChart from "./pages/GanttChart";
import Vendors from "./pages/Vendors";
import VendorDetail from "./pages/VendorDetail";
import VendorCompare from "./pages/VendorCompare";
import Expenditure from "./pages/Expenditure";
import Proposals from "./pages/Proposals";
import Reports from "./pages/Reports";
import FFELibrary from "./pages/FFELibrary";
import ProcurementPipeline from "./pages/ProcurementPipeline";
import AIAssistant from "./pages/AIAssistant";
import Contracts from "./pages/Contracts";
import ProductClipper from "./pages/ProductClipper";
import MagicLinkView from "./pages/MagicLinkView";
import Settings from "./pages/Settings";
import CRM from "./pages/CRM";
import Assemblies from "./pages/Assemblies";
import ProjectTemplates from "./pages/ProjectTemplates";
import BulkImport from "./pages/BulkImport";
import CalendarSync from "./pages/CalendarSync";
import PurchaseOrders from "./pages/PurchaseOrders";
import HelpSupport from "./pages/HelpSupport";
import HelperChatBubble from "./components/HelperChatBubble";

// Router component that handles session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id - this handles OAuth callback
  // CRITICAL: This synchronous check prevents race conditions by processing session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      
      {/* Protected routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/client-dashboard" 
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/projects" 
        element={
          <ProtectedRoute>
            <Projects />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/projects/:projectId" 
        element={
          <ProtectedRoute>
            <ProjectDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <Tasks />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/users" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Users />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoices" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <Invoices />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/calendar" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <CalendarView />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/gantt" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <GanttChart />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/vendors" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <Vendors />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/vendors/:vendorId" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <VendorDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/vendor-compare" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <VendorCompare />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/expenditure" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <Expenditure />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/proposals" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <Proposals />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <Reports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ffe-library" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <FFELibrary />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/procurement" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <ProcurementPipeline />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ai-assistant" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AIAssistant />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contracts" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <Contracts />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/product-clipper" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <ProductClipper />
          </ProtectedRoute>
        } 
      />
      <Route path="/share/:token" element={<MagicLinkView />} />
      <Route 
        path="/crm" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <CRM />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/assemblies" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <Assemblies />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/project-templates" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <ProjectTemplates />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/bulk-import" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <BulkImport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/calendar-sync" 
        element={
          <ProtectedRoute>
            <CalendarSync />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchase-orders" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <PurchaseOrders />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/help" 
        element={
          <ProtectedRoute>
            <HelpSupport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback */}
      <Route path="*" element={<Landing />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <HelperChatBubble />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
